import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { LogIn, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, CardContent } from "@/components/ui/Card";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LoginEntry {
  id: string;
  user_id: string;
  login_time: string;
  ip_address: string;
  user_agent: string;
  success: number;
  failure_reason: string | null;
  user_email?: string;
  user_name?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LoginLog() {
  const [page, setPage] = useState(1);
  const [successFilter, setSuccessFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const params: Record<string, string> = { page: String(page), limit: "50" };
  if (successFilter) params.success = successFilter;
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;

  const { data, isLoading } = useQuery<{
    data: LoginEntry[];
    meta: { total: number; page: number; limit: number };
  }>({
    queryKey: ["login-log", page, successFilter, startDate, endDate],
    queryFn: () => api.get("/admin/audit/login-log", { params }),
  });

  const entries = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const totalPages = Math.ceil(total / 50);

  // Failed logins summary
  const { data: failedData } = useQuery<{ data: LoginEntry[] }>({
    queryKey: ["failed-logins"],
    queryFn: () => api.get("/admin/audit/failed-logins", { params: { days: "7" } }),
  });

  const failedCount = failedData?.data?.length ?? 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Login History</h2>
        <p className="text-sm text-muted-foreground">
          Track user login attempts and identify suspicious activity
        </p>
      </div>

      {/* Failed login alert */}
      {failedCount > 0 && (
        <div className="flex items-center gap-3 rounded-md border border-yellow-200 bg-yellow-50 p-4">
          <AlertTriangle className="h-5 w-5 text-yellow-600" />
          <div>
            <p className="text-sm font-medium text-yellow-800">
              {failedCount} failed login attempt{failedCount !== 1 ? "s" : ""} in the last 7 days
            </p>
            <p className="text-xs text-yellow-700">
              Review below for potential unauthorized access attempts.
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Status</label>
              <select
                value={successFilter}
                onChange={(e) => { setSuccessFilter(e.target.value); setPage(1); }}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">All</option>
                <option value="true">Successful</option>
                <option value="false">Failed</option>
              </select>
            </div>
            <Input
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
            />
            <Input
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
            />
          </div>
        </CardContent>
      </Card>

      {isLoading && <div className="h-64 animate-pulse rounded-lg border bg-muted" />}

      {!isLoading && entries.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <LogIn className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">No login entries found.</p>
          </CardContent>
        </Card>
      )}

      {!isLoading && entries.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Time</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">User</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Status</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">IP Address</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Reason</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                      {new Date(entry.login_time).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium">{entry.user_name ?? "Unknown"}</p>
                        <p className="text-xs text-muted-foreground">{entry.user_email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
                          entry.success
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800",
                        )}
                      >
                        {entry.success ? "Success" : "Failed"}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                      {entry.ip_address}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {entry.failure_reason ?? "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages} ({total} entries)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
